<template>
  <div
    class="weather-widget"
    :style="{
      background: backgroundStyle,
      color: fontColor,
      fontFamily: fontFamily,
      borderRadius:
        typeof borderRadius === 'number' ? borderRadius + 'px' : borderRadius,
      padding: `${Math.floor(
        Math.min(props.height * 0.1, props.width * 0.05)
      )}px`,
    }"
    ref="widgetContainer"
  >
    <div class="content-container">
      <div
        class="info-column"
        :style="{
          gap: `${Math.floor(
            Math.min(props.height * 0.05, props.width * 0.02)
          )}px`,
        }"
      >
        <div class="city-name" :style="{ fontSize: `${cityNameSize}px` }">
          <LocationIcon
            :width="cityNameSize"
            :height="cityNameSize"
            :color="fontColor"
            :strokeWidth="1.5"
          />
          <span>{{ location }}</span>
          <!-- <span  :style="{ fontSize: `${dateTimeSize}px` }">{{ weatherStatus }}</span> -->
        </div>
        <!-- <div
          class="date-time"
          :style="{ fontSize: `${dateTimeSize}px` }"
          v-if="showDateTime"
        >
          {{ weatherStatus }}
        </div> -->

        <div class="temperature" :style="{ fontSize: `${tempSize}px` }">
          {{ temperature }}<span class="degree">°</span>
        </div>
      </div>
      <div class="icon-column">
        <div
          class="weather-icon"
          :style="{
            width: `${iconSize}px`,
            height: `${iconSize}px`,
          }"
        >
          <img
            v-if="weatherIcon"
            :src="weatherIcon"
            alt="Weather condition"
            style="width: 100%; height: 100%"
            class="weather-icon-img"
          />
          <div
            v-else
            class="icon-placeholder"
            :style="{ width: `${iconSize}px`, height: `${iconSize}px` }"
          ></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, defineExpose } from "vue";

export default defineComponent({
  name: "WeatherWidget",
});
</script>

<script setup lang="ts">
import { useI18n } from "vue-i18n";

const { t, locale } = useI18n();

import {
  ref,
  computed,
  onMounted,
  watch,
  onBeforeUnmount,
  defineProps,
  defineEmits,
} from "vue";
import { displayService } from "@/_services";
import { updateForecastData, updateForecastColors } from "@/eventBus";
import LocationIcon from "@/components/Icons/LocationIcon.vue";

const props = defineProps({
  city: {
    type: String,
    default: "Tunis",
  },
  unit: {
    type: String,
    default: "Celsius",
  },
  fontFamily: {
    type: String,
    default: "Arial",
  },
  fontSize: {
    type: [Number, String],
    default: 40,
  },
  fontColor: {
    type: String,
    default: "#FFFFFF",
  },
  backgroundColor: {
    type: String,
    default: "#03A9F4",
  },
  opacity: {
    type: Number,
    default: 1,
  },
  height: {
    type: Number,
    default: 200,
  },
  width: {
    type: Number,
    default: 300,
  },
  borderRadius: {
    type: Number,
    default: 0,
  },
  config: {
    type: Object,
    default: () => ({}),
  },
});
const emit = defineEmits(["update:forecastData"]);

const getBackgroundColorWithOpacity = (bgColor: any) => {
  const opacityValue = props.opacity / 100;

  //bgcolor rgba/hsla=> return as it is
  if (bgColor.includes("rgba") || bgColor.includes("hsla")) {
    return bgColor;
  }

  //convert hex to rgba
  if (bgColor.startsWith("#")) {
    const hex = bgColor.replace("#", "");
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacityValue})`;
  }

  return bgColor;
};

function hexToRgba(hex: string, alpha: number) {
  let c = hex.replace("#", "");
  if (c.length === 3)
    c = c
      .split("")
      .map((x) => x + x)
      .join("");
  const num = parseInt(c, 16);
  const r = (num >> 16) & 255;
  const g = (num >> 8) & 255;
  const b = num & 255;
  return `rgba(${r},${g},${b},${alpha})`;
}

const backgroundStyle = computed(() => {
  // gradient
  if (props.config.bgConfig?.selectedOption == "gradient") {
    const gradient = props.config.bgConfig.gradient;
    const opacityValue = props.opacity / 100;

    // Apply global opacity to gradient colors
    const start = hexToRgba(
      gradient.startColor,
      (gradient.startColorOpacity || 1) * opacityValue
    );
    const end = hexToRgba(
      gradient.endColor,
      (gradient.endColorOpacity || 1) * opacityValue
    );

    //direction
    let linearDirection = "to right";
    let radialPosition = gradient.direction || "center";

    switch (gradient.direction) {
      case "left-right":
        linearDirection = "to right";
        break;
      case "right-left":
        linearDirection = "to left";
        break;
      case "top-bottom":
        linearDirection = "to bottom";
        break;
      case "bottom-top":
        linearDirection = "to top";
        break;
      case "top-left-bottom-right":
        linearDirection = "135deg";
        break;
      case "top-right-bottom-left":
        linearDirection = "225deg";
        break;
      case "bottom-left-top-right":
        linearDirection = "45deg";
        break;
      case "bottom-right-top-left":
        linearDirection = "315deg";
        break;
      default:
        linearDirection = "to right";
    }

    if (gradient.type === "linear") {
      return `linear-gradient(${linearDirection}, ${start}, ${end})`;
    } else {
      //radial
      return `radial-gradient(circle at ${radialPosition}, ${start}, ${end})`;
    }
  }

  //bgColor
  if (props.config.bgConfig?.selectedOption == "color") {
    return getBackgroundColorWithOpacity(props.config.bgConfig.fillColor);
  }

  //image
  if (props.config.bgConfig?.selectedOption == "image") {
    const imageConfig = props.config.bgConfig.image;

    if (imageConfig.src && imageConfig.src.length > 0) {
      const imageUrl = imageConfig.src[0].url ? imageConfig.src[0].url : imageConfig.src[0];
      console.log("imageUrl", imageUrl);
      console.log("Decoded imageUrl", decodeURIComponent(imageUrl));

      // Get background properties from config or use defaults
      const backgroundSize = imageConfig.backgroundSize || "cover";
      const backgroundPosition = imageConfig.backgroundPosition || "center";
      const backgroundRepeat = imageConfig.backgroundRepeat || "no-repeat";

      const backgroundString = `url(${imageUrl}) ${backgroundPosition} / ${backgroundSize} ${backgroundRepeat}`;
      console.log("Final background string:", backgroundString);
      return backgroundString;
    }

    // Fallback if no image is available
    return "transparent";
  }

  //og fill color
  return getBackgroundColorWithOpacity(props.backgroundColor);
});

//sizing
const widgetContainer = ref(null);
const containerWidth = ref(props.width);
const containerHeight = ref(props.height);

//if date/time shown based on container size
const showDateTime = computed(() => {
  return containerWidth.value > 150 && containerHeight.value > 120;
});

// size calcul
const tempSize = computed(() => {
  const height = props.height || 200;
  const width = props.width || 300;
  // 40%height and 20%width
  return Math.floor(Math.min(height * 0.4, width * 0.2));
});

const iconSize = computed(() => {
  const height = props.height || 200;
  const width = props.width || 300;
  // 90%height and 30%width
  return Math.floor(Math.min(height * 0.9, width * 0.3));
});

const cityNameSize = computed(() => {
  const height = props.height || 200;
  const width = props.width || 300;
  // 20%height and 10%width
  return Math.floor(Math.min(height * 0.2, width * 0.1));
});

const dateTimeSize = computed(() => {
  const height = props.height || 200;
  const width = props.width || 300;
  // 15%height and 8%width
  return Math.floor(Math.min(height * 0.15, width * 0.08));
});

const weatherStatus = computed(() => {
  if (!weatherData.value) return "--";

  return t(weatherData.value.current.condition.text);
});

const language = ref("en");
const getLanguage = () => {
  const lang = localStorage.getItem("Language") || "en";
  if (lang == "fr") {
    language.value = "fr";
    locale.value = "fr";
  } else if (lang == "ar") {
    language.value = "ar";
    locale.value = "ar";
  } else {
    language.value = "en";
    locale.value = "en";
  }
};

let intervalId: number | undefined;

function scheduleWeatherFetch() {
  if (intervalId) clearInterval(intervalId);

  // init
  fetchWeatherData();

  // fecth every 2 hours
  intervalId = setInterval(() => {
    fetchWeatherData();
  }, 2 * 60 * 60 * 1000);
}

onMounted(() => {
  getLanguage();

  scheduleWeatherFetch();
});

onBeforeUnmount(() => {
  if (intervalId) clearInterval(intervalId);
});

const weatherData: any = ref(null);
const loading = ref(false);
const error = ref<any>(null);

const location = computed(() => {
  return weatherData.value?.location?.name || props.city;
});

const temperature = computed(() => {
  if (!weatherData.value) return "--";

  if (props.unit === "Celsius") {
    return Math.round(weatherData.value.current.temp_c);
  } else {
    return Math.round(weatherData.value.current.temp_f);
  }
});

const weatherIcon = computed(() => {
  if (!weatherData.value) return null;
  const condition = weatherData.value.current.condition.code;
  try {
    if (condition === 1000) {
      return require("@/assets/images/weather/sunny.png");
    } else if (condition == 1006) {
      return require("@/assets/images/weather/cloudy.png");
    } else if (condition == 1009 || condition == 1030 || condition == 1147) {
      return require("@/assets/images/weather/overcast.png");
    } else if (condition === 1003) {
      return require("@/assets/images/weather/partly_cloudy.png");
    } else if (
      condition == 1273 ||
      condition == 1252 ||
      condition == 1249 ||
      condition === 1246 ||
      condition === 1240 ||
      condition == 1201 ||
      condition == 1198 ||
      condition === 1195 ||
      condition === 1192 ||
      condition === 1189 ||
      condition === 1186 ||
      condition == 1183 ||
      condition == 1180 ||
      condition === 1153 ||
      condition === 1150 ||
      condition == 1063 ||
      condition === 1289
    ) {
      return require("@/assets/images/weather/rainy.png");
    } else if (
      condition == 1264 ||
      condition === 1261 ||
      condition == 1258 ||
      condition == 1255 ||
      condition === 1243 ||
      condition === 1225 ||
      condition === 1222 ||
      condition === 1219 ||
      condition === 1216 ||
      condition == 1213 ||
      condition == 1210 ||
      condition === 1066 ||
      condition === 1114 ||
      condition === 1117
    ) {
      return require("@/assets/images/weather/snowy.png");
    } else if (
      condition == 1237 ||
      condition == 1168 ||
      condition == 1069 ||
      condition == 1072 ||
      condition === 1279 ||
      condition === 1282
    ) {
      return require("@/assets/images/weather/ice.png");
    } else if (
      condition == 1087 ||
      condition == 1276 ||
      condition == 1279 ||
      condition == 1282
    ) {
      return require("@/assets/images/weather/thunder_storm.png");
    } else if (condition == 1204 || condition == 1207) {
      return require("@/assets/images/weather/windy.png");
    } else {
      return `https:${weatherData.value.current.condition.icon}`;
    }
  } catch (e) {
    return `https:${weatherData.value.current.condition.icon}`;
  }
});

const formattedDateTime = computed(() => {
  if (!weatherData.value?.location?.localtime) {
    const now = new Date();
    return `${now.getHours().toString().padStart(2, "0")}:${now
      .getMinutes()
      .toString()
      .padStart(2, "0")} • ${now.getDate().toString().padStart(2, "0")}/${(
      now.getMonth() + 1
    )
      .toString()
      .padStart(2, "0")}`;
  }

  const dateTime = new Date(weatherData.value.location.localtime);
  const hours = dateTime.getHours().toString().padStart(2, "0");
  const minutes = dateTime.getMinutes().toString().padStart(2, "0");
  const day = dateTime.getDate().toString().padStart(2, "0");
  const month = (dateTime.getMonth() + 1).toString().padStart(2, "0");

  return `${hours}:${minutes} • ${day}/${month}`;
});

const forecastData: any = ref({});
const fetchWeatherData = async () => {
  try {
    loading.value = true;
    const res = await displayService.getWeatherData(
      props.city,
      language.value,
      3,
      "no"
    );
    weatherData.value = res.data;
    forecastData.value = res.data.forecast;
    loading.value = false;
    // update event bus with the forecast data
    updateForecastData(res.data.forecast);
    console.log(
      "WeatherWidget: Updated forecast data in event bus:",
      res.data.forecast
    );
  } catch (err) {
    console.error("Error fetching weather data:", err);
    loading.value = false;
  }
};

watch(
  () => props.city,
  () => {
    fetchWeatherData();
  }
);

watch(
  () => forecastData.value,
  (newVal) => {
    if (newVal && newVal.forecastday) {
      emit("update:forecastData", newVal.forecastday);
    }
  },
  { deep: true }
);

watch(
  () => [props.backgroundColor, props.fontColor],
  ([newBgColor, newFontColor]) => {
    updateForecastColors({
      backgroundColor: newBgColor,
      fontColor: newFontColor,
      opacity: props.opacity,
    });
  },
  { immediate: true }
);

defineExpose({
  forecastData,
  loading,
});

// watch(
//   () => locale.value,
//   (newLocale) => {
//     console.log('Locale changed to:', newLocale);
//     fetchWeatherData();
//   }
// );
</script>


<style scoped>
.weather-widget {
  border-radius: 10px;
  display: flex !important;
  width: 100%;
  height: 100%;
  overflow: hidden;
  box-sizing: border-box;
}

.content-container {
  display: flex !important;
  width: 100%;
  height: 100%;
  justify-content: space-between;
  align-items: center;
  box-sizing: border-box;
}

.info-column {
  display: flex !important;
  flex-direction: column;
  justify-content: space-between;
  flex: 1;
}

.icon-column {
  display: flex !important;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.city-name {
  font-weight: 500;
  display: flex !important;
  flex-direction: row;
  align-items: center;
}

.date-time {
  opacity: 0.9;
  margin-bottom: 3px;
  display: flex !important;
}

.temperature {
  font-weight: bold;
  line-height: 1;
  display: flex !important;
}

.degree {
  font-weight: normal;
}

.weather-icon {
  box-sizing: border-box;
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.weather-icon-img {
  object-fit: contain;
  max-width: 100%;
  max-height: 100%;
  display: block;
}

.icon-placeholder {
  background-color: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex !important;
  align-items: center;
  justify-content: center;
  font-size: 1.5em;
}
</style>